import { FunctionComponent, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	faCodeMerge,
	faPencil,
	faTrash,
	faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { LEAD_SCORING_REGIONS, REST_OF_THE_WORLD } from "@signalist/shared-types";

import { Icon, Modal } from "@/components";
import { classNames } from "@/utils/classNames";

import {
	tierChipClassName,
	TierRow,
} from "@/pages/Private/pages/OnboardingTour/components/steps/scoring/CriterionCard";
import {
	ListCriterionRef,
	readTierValues,
	Tier,
	TIERS,
	writeTierValues,
} from "@/pages/Private/pages/OnboardingTour/components/steps/scoring/criteriaTiers";
import {
	LINKEDIN_SIZE_BUCKETS,
	readSizeTiers,
	toggleSizeBucket,
	unassignedSizeBuckets,
	writeSizeTiers,
} from "@/pages/Private/pages/OnboardingTour/components/steps/scoring/sizeBuckets";
import { TaxonomyChipInput } from "@/pages/Private/pages/OnboardingTour/components/steps/scoring/TaxonomyChipInput";
import { moveTierValue } from "@/pages/Private/pages/OnboardingTour/components/steps/scoring/tierDragDrop";

import { ScoreCriteriaType } from "../../schema/score.schema";
import { DetailHeader } from "../../shell/DetailHeader";

import { AiCriterionEditor } from "./AiCriterionEditor";
import {
	copyHqToPersonLocation,
	CriterionRef,
	criterionRefKey,
	ifNotFoundOf,
	isCriterionOn,
	readSeniorityTiers,
	renameAiCriterion,
	SENIORITY_POOL,
	setCriterionOn,
	setIfNotFound,
	toggleFixedValue,
	unassignedFixedValues,
	writeSeniorityTiers,
} from "./criteriaDraft";
import { FixedPoolSelect } from "./FixedPoolSelect";

interface CriterionEditorProps {
	scoring: ScoreCriteriaType;
	criterionRef: CriterionRef;
	label: string;
	onChange: (next: ScoreCriteriaType) => void;
	/** AI criteria can be removed; the fixed ones only switched off. */
	onRemove?: () => void;
}

const LOCATION_EXTRAS = [REST_OF_THE_WORLD, ...LEAD_SCORING_REGIONS];
const SIZE_POOL = LINKEDIN_SIZE_BUCKETS.map((bucket) => bucket.label);

const ACTION =
	"flex h-6 cursor-pointer items-center gap-1 rounded-md border px-2 text-[11px] font-medium";

/**
 * The card for the selected criterion (PREC-4055): its activate toggle, the
 * bucket editor that fits the criterion — chips with drag and drop for
 * locations and industries, a fixed-pool picker whose chips drag the same way
 * for size and seniority, free text for AI criteria — and the "if not found"
 * rule. Editing a switched-off
 * criterion asks whether to activate it, so a change never silently grades
 * nothing.
 */
export const CriterionEditor: FunctionComponent<CriterionEditorProps> = ({
	scoring,
	criterionRef,
	label,
	onChange,
	onRemove,
}) => {
	const { t } = useTranslation();
	const [renameSignal, setRenameSignal] = useState(0);
	const [pending, setPending] = useState<ScoreCriteriaType | null>(null);
	const [leftOff, setLeftOff] = useState<string | null>(null);

	const refKey = criterionRefKey(criterionRef);
	const on = isCriterionOn(scoring, criterionRef);
	const isAi = criterionRef.key === "OTHER";

	// A grade on a switched-off criterion is held back until the user decides.
	const apply = (next: ScoreCriteriaType) => {
		if (on || leftOff === refKey) {
			onChange(next);
		} else {
			setPending(next);
		}
	};

	const bucketPlaceholder = (tier: Tier) =>
		t(
			tier === 0
				? "leadScoring.scoringPage.tierDisqualifyPlaceholder"
				: "leadScoring.scoringPage.tierPlaceholder"
		);

	const listRows = (kind: "country" | "industry"): ReactNode => {
		const listRef: ListCriterionRef = {
			section: criterionRef.section,
			key: criterionRef.key as ListCriterionRef["key"],
			field: kind === "industry" ? "industries" : "countries",
		};

		return TIERS.map((tier) => (
			<TierRow key={tier} label={t(`leadScoring.scoringPage.tiers.${tier}`)} tier={tier}>
				<TaxonomyChipInput
					// A location the list does not know is scored as written, as an industry is (PREC-4515).
					allowFreeText
					chipClassName={tierChipClassName(tier)}
					dragGroup={listRef.key}
					extraOptions={kind === "country" ? LOCATION_EXTRAS : undefined}
					kind={kind}
					name={`${refKey}-${tier}`}
					placeholder={bucketPlaceholder(tier)}
					tier={tier}
					values={readTierValues(scoring, listRef, tier)}
					onChange={(values) => apply(writeTierValues(scoring, listRef, tier, values))}
					onMoveHere={(payload) =>
						apply(moveTierValue(scoring, listRef, payload.tier, tier, payload.value))
					}
				/>
			</TierRow>
		));
	};

	const sizeRows = (): ReactNode => {
		const tiers = readSizeTiers(scoring);
		const unassigned = unassignedSizeBuckets(tiers);

		return (
			<>
				{TIERS.map((tier) => (
					<TierRow key={tier} label={t(`leadScoring.scoringPage.tiers.${tier}`)} tier={tier}>
						<FixedPoolSelect
							dragGroup={criterionRef.key}
							name={`size-${tier}`}
							placeholder={bucketPlaceholder(tier)}
							pool={SIZE_POOL}
							tier={tier}
							tiers={tiers}
							onMoveHere={(payload) =>
								payload.tier !== tier &&
								apply(writeSizeTiers(scoring, toggleSizeBucket(tiers, tier, payload.value)))
							}
							onToggle={(value) =>
								apply(writeSizeTiers(scoring, toggleSizeBucket(tiers, tier, value)))
							}
						/>
					</TierRow>
				))}
				{on && unassigned.length > 0 && (
					// PREC-4127: the same warning the onboarding grading screen carries
					// (PREC-3896), which this editor never had. A range in no tier answers
					// -2 for a size the engine *did* find, and -2 never disqualifies — so
					// assigning the eight ranges a user recognises leaves the ninth
					// scoring nothing, and "if we can't find this information" does not
					// cover it. A warning, never a gate: leaving a range out is allowed.
					<p
						className="mt-1 flex items-start gap-2 rounded-lg border border-tag-warning-border bg-background-warning px-3 py-2 text-xs text-tag-warning-dark"
						data-testid="size-unassigned-warning"
					>
						<Icon className="mt-0.5 h-3 w-3 shrink-0" icon={faTriangleExclamation} />
						{t("leadScoring.scoringPage.sizeUnassigned", {
							count: unassigned.length,
							ranges: unassigned.join(", "),
						})}
					</p>
				)}
			</>
		);
	};

	const seniorityRows = (): ReactNode => {
		const tiers = readSeniorityTiers(scoring);
		const unassigned = unassignedFixedValues(SENIORITY_POOL, tiers);

		return (
			<>
				{TIERS.map((tier) => (
					<TierRow key={tier} label={t(`leadScoring.scoringPage.tiers.${tier}`)} tier={tier}>
						<FixedPoolSelect
							dragGroup={criterionRef.key}
							name={`seniority-${tier}`}
							placeholder={bucketPlaceholder(tier)}
							pool={SENIORITY_POOL}
							tier={tier}
							tiers={tiers}
							onMoveHere={(payload) =>
								payload.tier !== tier &&
								apply(writeSeniorityTiers(scoring, toggleFixedValue(tiers, tier, payload.value)))
							}
							onToggle={(value) =>
								apply(writeSeniorityTiers(scoring, toggleFixedValue(tiers, tier, value)))
							}
						/>
					</TierRow>
				))}
				{unassigned.length > 0 && (
					<p className="text-[11px] text-text-meta" data-testid="seniority-unassigned">
						{t("leadScoring.scoringPage.unassigned", { count: unassigned.length })}{" "}
						<span className="text-text-meta">
							{t("leadScoring.scoringPage.unassignedIntro")} {unassigned.join(", ")}
						</span>
					</p>
				)}
			</>
		);
	};

	const body = (): ReactNode => {
		switch (criterionRef.key) {
			case "OTHER":
				return <AiCriterionEditor criterionRef={criterionRef} scoring={scoring} onChange={apply} />;
			case "INDUSTRY":
				return listRows("industry");
			case "EMPLOYEE_SIZE":
				return sizeRows();
			case "JOB_LEVEL":
				return seniorityRows();
			default:
				return listRows("country");
		}
	};

	const ifNotFound = ifNotFoundOf(scoring, criterionRef);
	const ruleButton = (value: "Ignore" | "Disqualified", text: string) => (
		<button
			aria-pressed={ifNotFound === value}
			className={classNames(
				"h-7 cursor-pointer px-3 text-[11px] font-medium",
				ifNotFound === value ? "bg-blue-action-200 text-brand-light" : "bg-white text-text-meta"
			)}
			data-testid={`if-not-found-${value}`}
			type="button"
			onClick={() => apply(setIfNotFound(scoring, criterionRef, value))}
		>
			{text}
		</button>
	);

	return (
		<div
			className={classNames(
				"mt-3 overflow-hidden rounded-lg border border-border-light bg-white",
				!on && "opacity-90"
			)}
			data-testid={`criterion-editor-${refKey}`}
		>
			<DetailHeader
				active={on}
				activeTitle={t("leadScoring.scoringPage.criterionActive")}
				inactiveTitle={t("leadScoring.scoringPage.criterionInactive")}
				name={label}
				renameSignal={renameSignal}
				testId="criterion-detail"
				actions={
					isAi && (
						<>
							<button
								className={classNames(
									ACTION,
									"border-transparent text-text-meta hover:bg-background-light"
								)}
								type="button"
								onClick={() => setRenameSignal((n) => n + 1)}
							>
								<Icon className="h-3 w-3" icon={faPencil} />
								{t("leadScoring.detail.rename")}
							</button>
							<button
								className={classNames(
									ACTION,
									"border-status-error-light text-status-error hover:bg-error-light"
								)}
								data-testid="criterion-delete"
								type="button"
								onClick={onRemove}
							>
								<Icon className="h-3 w-3" icon={faTrash} />
								{t("leadScoring.scoringPage.deleteCriterion")}
							</button>
						</>
					)
				}
				onRename={
					isAi ? (name) => onChange(renameAiCriterion(scoring, criterionRef, name)) : undefined
				}
				onToggle={(active) => onChange(setCriterionOn(scoring, criterionRef, active))}
			/>
			<div className={classNames("flex flex-col gap-2 px-3.5 py-3.5", !on && "opacity-60")}>
				{body()}

				<div className="mt-2 flex flex-wrap items-center gap-2.5 text-xs text-text-meta">
					<span>{t("leadScoring.scoringPage.ifNotFound")}</span>
					<span className="flex overflow-hidden rounded-md border border-border-light">
						{ruleButton("Ignore", t("leadScoring.scoringPage.ifNotFoundIgnore"))}
						{ruleButton("Disqualified", t("leadScoring.scoringPage.ifNotFoundDisqualify"))}
					</span>
				</div>

				{criterionRef.key === "PERSON_LOCATION" && (
					<button
						className={classNames(
							ACTION,
							"mt-1 w-fit border-border-light bg-white text-text hover:bg-background-light"
						)}
						data-testid="use-hq-buckets"
						type="button"
						onClick={() => apply(copyHqToPersonLocation(scoring))}
					>
						<Icon className="h-3 w-3" icon={faCodeMerge} />
						{t("leadScoring.scoringPage.useHqBuckets")}
					</button>
				)}
			</div>

			{pending && (
				<Modal
					isOpened
					cancelButtonText={t("basics.cancel")}
					secondSubmitButtonText={t("leadScoring.scoringPage.leaveOff")}
					size="sm"
					submitButtonText={t("leadScoring.scoringPage.activateAndContinue")}
					title={t("leadScoring.scoringPage.activateTitle", {
						criterion: label,
						scoring: scoring.name,
					})}
					handleClose={() => setPending(null)}
					handleSave={() => {
						onChange(setCriterionOn(pending, criterionRef, true));
						setPending(null);
					}}
					handleSecondSave={() => {
						onChange(pending);
						setLeftOff(refKey);
						setPending(null);
					}}
				>
					<p className="text-sm text-text-meta">{t("leadScoring.scoringPage.activateBody")}</p>
				</Modal>
			)}
		</div>
	);
};
